/**
 * Решения «мелкая ли картинка» и «во сколько раз её увеличить» — чистые функции,
 * без канвы и модели.
 *
 * Сравнивается **наибольшая** сторона: картинка вписывается в плейсхолдер без
 * обрезки и упирается в него длинной стороной.
 */

import { ImageSize } from 'shared/utils/image/image-canvas';
import {
  ImageKind,
  MAX_UPSCALE,
  MIN_UPSCALE,
  SMALL_IMAGE_MAX_SIDE,
  UPSCALE_TARGET_SIDE,
  WASM_SECONDS_PER_TILE,
} from 'shared/utils/image/upscale/constants';

function longestSide(size: ImageSize): number {
  return Math.max(size.width, size.height);
}

/** Мелкая — наибольшая сторона меньше порога рода. Пустая картинка мелкой не считается. */
export function isSmallImage(size: ImageSize, kind: ImageKind): boolean {
  const longest = longestSide(size);
  return longest > 0 && longest < SMALL_IMAGE_MAX_SIDE[kind];
}

/**
 * Во сколько раз увеличить, чтобы наибольшая сторона дошла до цели: от ×2 до ×4.
 *
 * Больше ×4 не берём, даже если модель умеет ×8: дальше она только дорисовывает. Меньше ×2 не берём:
 * картинке почти у порога и ×2 даёт заметный запас, а дробный ×1,2 ничего не меняет.
 */
export function chooseScale(size: ImageSize, targetSide: number): number {
  const longest = longestSide(size);
  if (longest <= 0) return MIN_UPSCALE;

  return Math.min(MAX_UPSCALE, Math.max(MIN_UPSCALE, targetSide / longest));
}

/** Размер результата: исходник × `chooseScale`, стороны целые и не меньше 1. */
export function targetSize(size: ImageSize, targetSide: number): ImageSize {
  const scale = chooseScale(size, targetSide);

  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

/** Цель по умолчанию для рода. */
export function defaultTargetSide(kind: ImageKind): number {
  return UPSCALE_TARGET_SIDE[kind];
}

/**
 * Сколько секунд осталось считать без WebGPU: несделанные плитки × замер «Скорость».
 * Оценка сверху — замер снят на быстрой машине, на слабой будет дольше.
 */
export function estimateWasmSeconds(kind: ImageKind, remainingTiles: number): number {
  return Math.max(0, remainingTiles) * WASM_SECONDS_PER_TILE[kind];
}
